import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Container,
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as CopyIcon,
  LinkOff as RevokeIcon,
} from '@mui/icons-material';
import { authAPI, shareLinkAPI } from '../services/api';
import { colors } from '../theme';

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('zh-HK', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const AdminSettingsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [label, setLabel] = useState('');
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);

  React.useEffect(() => {
    if (!authAPI.isAuthenticated() || !authAPI.isAdmin()) {
      navigate('/');
    }
  }, [navigate]);

  const { data: links = [], isLoading, error } = useQuery(
    'share-links',
    () => shareLinkAPI.list().then((res) => res.data),
  );

  const createMutation = useMutation(
    (data) => shareLinkAPI.create(data),
    {
      onSuccess: (res) => {
        const token = res.data.token;
        const url = `${window.location.origin}/auth/share-link?token=${encodeURIComponent(token)}`;
        setNewLinkUrl(url);
        setLabel('');
        queryClient.invalidateQueries('share-links');
      },
    },
  );

  const revokeMutation = useMutation(
    (id) => shareLinkAPI.revoke(id),
    {
      onSuccess: () => {
        setRevokeTarget(null);
        queryClient.invalidateQueries('share-links');
      },
    },
  );

  const handleCreate = () => {
    createMutation.mutate({ label: label.trim() });
  };

  const handleCopy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {
      // fallback for older browsers
      const el = document.createElement('textarea');
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const handleCloseNewLinkDialog = () => {
    setNewLinkUrl('');
    createMutation.reset();
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, color: colors.royalBlue, mb: 1 }}>
        Settings
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        管理系統設定及分享登入連結
      </Typography>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
          分享登入連結 Shareable Login Links
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          建立連結後，任何人開啟連結即可直接登入系統（以一般會員身份，無管理員權限）。連結可隨時撤銷。
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap', mb: 3 }}>
          <TextField
            label="備註（選填）"
            placeholder="例如：給義工使用"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            size="small"
            sx={{ minWidth: 280, flex: 1 }}
          />
          <Button
            variant="contained"
            startIcon={createMutation.isLoading ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
            onClick={handleCreate}
            disabled={createMutation.isLoading}
            sx={{ backgroundColor: colors.royalBlue }}
          >
            建立連結
          </Button>
        </Box>

        {createMutation.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {createMutation.error?.response?.data?.detail || '建立連結失敗'}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            無法載入連結列表
          </Alert>
        )}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>備註</TableCell>
                  <TableCell>建立者</TableCell>
                  <TableCell>建立時間</TableCell>
                  <TableCell>最後使用</TableCell>
                  <TableCell>狀態</TableCell>
                  <TableCell align="right">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {links.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                      尚未建立任何分享連結
                    </TableCell>
                  </TableRow>
                ) : (
                  links.map((link) => (
                    <TableRow key={link.id} sx={{ opacity: link.is_active ? 1 : 0.6 }}>
                      <TableCell>{link.label || '—'}</TableCell>
                      <TableCell>{link.created_by_username}</TableCell>
                      <TableCell>{formatDate(link.created_at)}</TableCell>
                      <TableCell>{formatDate(link.last_used_at)}</TableCell>
                      <TableCell>
                        {link.is_active ? (
                          <Chip label="有效" size="small" color="success" variant="outlined" />
                        ) : (
                          <Chip label="已撤銷" size="small" color="default" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {link.is_active && (
                          <Tooltip title="撤銷連結">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setRevokeTarget(link)}
                            >
                              <RevokeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* New link dialog — shown once after creation */}
      <Dialog open={!!newLinkUrl} onClose={handleCloseNewLinkDialog} maxWidth="sm" fullWidth>
        <DialogTitle>分享連結已建立</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            此連結只會顯示一次，請立即複製並妥善保存。關閉後無法再次查看完整連結。
          </Alert>
          <TextField
            fullWidth
            multiline
            minRows={2}
            value={newLinkUrl}
            InputProps={{ readOnly: true }}
            sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNewLinkDialog}>關閉</Button>
          <Button
            variant="contained"
            startIcon={<CopyIcon />}
            onClick={() => handleCopy(newLinkUrl)}
            sx={{ backgroundColor: colors.royalBlue }}
          >
            {copySuccess ? '已複製' : '複製連結'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke confirmation */}
      <Dialog open={!!revokeTarget} onClose={() => setRevokeTarget(null)}>
        <DialogTitle>撤銷連結？</DialogTitle>
        <DialogContent>
          <Typography>
            確定要撤銷「{revokeTarget?.label || '此連結'}」嗎？撤銷後該連結將無法再用於登入。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)}>取消</Button>
          <Button
            color="error"
            variant="contained"
            disabled={revokeMutation.isLoading}
            onClick={() => revokeMutation.mutate(revokeTarget.id)}
          >
            撤銷
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminSettingsPage;
